import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import { Plus, Minus, Users, Info } from 'lucide-react'; // Added Users and Info icons

// Initialize the API client
const client = new FastAPIClient();

/**
 * CreateRoles Component.
 * Allows users to create multiple role entries simultaneously.
 * Designed with Tailwind CSS.
 */
const CreateRoles = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // State to hold user info
    const [loggedInUser, setLoggedInUser] = useState(null);

    // Initial state for a single role entry
    const initialRole = {
        name: '',
        description: '',
    };

    // State to manage multiple role entries
    const [roles, setRoles] = useState([initialRole]);
    const [errors, setErrors] = useState({}); // Stores errors, keyed by row index and field name
    const [submitStatus, setSubmitStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'

    // Effect to load user from local storage
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (lcUser) {
            setLoggedInUser(JSON.parse(lcUser));
        } else {
            showToast("Please log in to create roles.", "info");
            // Optionally redirect to login if no user is found
            navigate('/login');
        }
    }, [showToast]);

    // Handler for changes in individual role input fields
    const handleRoleInputChange = useCallback((index, e) => {
        const { name, value } = e.target;
        const updatedRoles = roles.map((role, i) =>
            i === index ? { ...role, [name]: value } : role
        );
        setRoles(updatedRoles);

        // Clear specific field error as user types
        if (errors[index] && errors[index][name]) {
            setErrors(prevErrors => {
                const newRowErrors = { ...prevErrors[index], [name]: '' };
                if (Object.values(newRowErrors).every(err => !err)) {
                    const newErrors = { ...prevErrors };
                    delete newErrors[index];
                    return newErrors;
                }
                return { ...prevErrors, [index]: newRowErrors };
            });
        }
    }, [roles, errors]);

    // Adds a new empty role row
    const handleAddRoleRow = useCallback(() => {
        setRoles(prevRoles => [
            ...prevRoles,
            { ...initialRole }
        ]);
    }, [initialRole]);

    // Removes a role row by index
    const handleRemoveRoleRow = useCallback((index) => {
        setRoles(prevRoles => prevRoles.filter((_, i) => i !== index));
        // Also remove errors for the deleted row
        setErrors(prevErrors => {
            const newErrors = { ...prevErrors };
            delete newErrors[index];
            return newErrors;
        });
    }, []);

    // Client-side validation for all role entries
    const validateForm = useCallback(() => {
        let newErrors = {};
        let isValid = true;

        if (roles.length === 0) {
            newErrors.general = "At least one role entry is required.";
            isValid = false;
        }

        roles.forEach((role, index) => {
            let rowErrors = {};
            if (!role.name.trim()) {
                rowErrors.name = 'Role Name is required.';
                isValid = false;
            }
            // Description is optional, so no validation for it
            if (Object.keys(rowErrors).length > 0) {
                newErrors[index] = rowErrors;
            }
        });

        setErrors(newErrors);
        return isValid;
    }, [roles]);

    // Form submission handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitStatus('loading');
        setErrors({}); // Clear previous errors

        if (!validateForm()) {
            setSubmitStatus('error');
            showToast("Please correct the errors in the form.", "error");
            return;
        }

        // Prepare payload
        const payload = roles.map(role => ({
            name: role.name,
            description: role.description || null, // Ensure null if empty
        }));

        console.log('Submitting roles payload:', payload);

        try {
            // Assuming the backend endpoint for creating multiple roles is '/roles/create_roles'
            const response = await client.postData('/auth/create-roles', payload);
            console.log('Roles created successfully:', response);
            showToast("Roles created successfully!", "success");

            // Clear the form by resetting to a single empty role
            setRoles([initialRole]);
            setSubmitStatus('success');
        } catch (err) {
            console.error('Failed to create roles:', err);
            const errorMessage = err.response?.data?.detail || `Failed to create roles: ${err.message || err}`;
            showToast(errorMessage, "error");
            setSubmitStatus('error');
        }
    };

    if (!loggedInUser) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to create roles.</p>
                <Button onClick={() => navigate('/login')} variant="primary">
                    Go to Login
                </Button>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <Card className="p-6">
                <h2 className="text-2xl font-bold text-center text-text-primary mb-6">
                    Create New Roles
                </h2>
                <p className="text-center text-text-secondary mb-8 text-sm">
                    Define new roles with specific names and descriptions for your organization.
                </p>

                <form onSubmit={handleSubmit} noValidate>
                    {roles.map((role, index) => (
                        <div key={index} className="flex flex-col md:flex-row items-end gap-4 mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50 relative">
                            {/* Remove button for all but the first row (if only one row, disable) */}
                            {roles.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => handleRemoveRoleRow(index)}
                                    className="absolute top-2 right-2 p-1 rounded-full text-gray-500 hover:bg-red-100 hover:text-gmail-red
                                               focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gmail-red"
                                    aria-label={`Remove role entry ${index + 1}`}
                                >
                                    <Minus size={20} />
                                </button>
                            )}
                            <div className="flex-1 w-full">
                                <Input
                                    label={`Role ${index + 1} Name`}
                                    id={`name-${index}`}
                                    name="name"
                                    type="text"
                                    placeholder="e.g., Administrator, Sales Manager"
                                    value={role.name}
                                    onChange={(e) => handleRoleInputChange(index, e)}
                                    required
                                    error={errors[index]?.name}
                                />
                                {errors[index]?.name && <p className="text-gmail-red text-xs mt-[-10px] mb-4">{errors[index].name}</p>}
                            </div>
                            <div className="flex-1 w-full">
                                <Input
                                    label={`Role ${index + 1} Description (Optional)`}
                                    id={`description-${index}`}
                                    name="description"
                                    type="text"
                                    placeholder="Brief description of this role"
                                    value={role.description}
                                    onChange={(e) => handleRoleInputChange(index, e)}
                                    error={errors[index]?.description}
                                />
                                {errors[index]?.description && <p className="text-gmail-red text-xs mt-[-10px] mb-4">{errors[index].description}</p>}
                            </div>
                        </div>
                    ))}

                    {errors.general && <p className="text-gmail-red text-sm text-center mb-4">{errors.general}</p>}

                    <div className="flex justify-between items-center mt-6">
                        <Button
                            type="button"
                            onClick={handleAddRoleRow}
                            variant="secondary"
                            className="flex items-center gap-2"
                        >
                            <Plus size={20} />
                            Add Another Role
                        </Button>

                        <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            disabled={submitStatus === 'loading'}
                            className="flex items-center gap-2"
                        >
                            <Users size={20} />
                            {submitStatus === 'loading' ? 'Creating Roles...' : 'Create Roles'}
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
    );
};

export default CreateRoles;
